import React from 'react';
import { Trans } from 'react-i18next';
import { format } from 'date-fns-tz';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-query';

import { ToastVariant, useDateFns, useToastMessage } from '~/Hooks';
import { createQueriedURL, routeReplacer } from '~/Helpers/String.helper';
import { EVENT_SERVICE_URLS } from '~/Constants/Urls.constants';
import { IEvent } from '~/Models/DTO/Event.model';
import { requestReactQuery } from '~/Helpers/ReactQuery.helper';
import { extractErrorMessage } from '~/Helpers/Error.helper';
import { IErrorResponse } from '~/Models/DTO/Error.models';
import { IEventSeries } from '~/Models/DTO/EventSeries.model';
import { REQUEST_ACTIONS, STATUS } from '~/Constants/Actions.constants';

interface IEventSeriesAction extends IEvent {
  to: string | null;
  from: string | null;
}

export const useSaveCalendarEventSeries = () => {
  const dispatch = useDispatch();
  const { showToast } = useToastMessage();
  const { convertToProviderTimezone } = useDateFns();

  const eventSeriesAction = useMutation(
    async ({
      id,
      to,
      end,
      from,
      start,
      eventSerieId,
    }: IEventSeriesAction): Promise<IEvent | void> => {
      const startDate = convertToProviderTimezone(start);
      const endDate = convertToProviderTimezone(end);
      const url = routeReplacer(EVENT_SERVICE_URLS.EVENT_SERIES_DETAIL, { id: eventSerieId });
      const prevEventSeries = await requestReactQuery<IEventSeries>({
        url,
        config: { method: 'GET' },
        apiVersion: '2',
      });
      const updatedEventSeries = await requestReactQuery<IEventSeries>({
        url,
        data: {
          ...prevEventSeries,
          dailyStartTime: format(new Date(startDate), 'HH:mm:ss'),
          dailyEndTime: format(new Date(endDate), 'HH:mm:ss'),
        },
        config: { method: 'PUT' },
      });
      const visibleCalendarEventsByEventSeries = await requestReactQuery<IEvent[]>({
        url: createQueriedURL(EVENT_SERVICE_URLS.EVENT_LIST, {
          to,
          from,
          providerId: updatedEventSeries.providerId,
          eventSerieIds: [eventSerieId],
        }),
        config: { method: 'GET' },
      });
      dispatch({
        type: REQUEST_ACTIONS.UPDATE_EVENT_LIST,
        name: REQUEST_ACTIONS.UPDATE_EVENT_LIST,
        status: STATUS.SUCCESS,
        payload: visibleCalendarEventsByEventSeries,
      });
      return visibleCalendarEventsByEventSeries.find((event) => event.id === id);
    },
    {
      onError: (errorObj: IErrorResponse) => {
        const message = extractErrorMessage(errorObj) as any;
        showToast(
          'Event update error',
          <Trans i18nKey={message?.key} defaults={message?.defaultMessage || message} />,
          ToastVariant.ERROR,
        );
      },
    },
  );

  return { eventSeriesAction };
};
